import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { WorkflowNodeRunOutput } from 'src/content/Workflows/canvas/workflowNodeCompletedOutput';
import {
  applyOutputRefRenames,
  collectFieldNameRenames
} from 'src/content/Workflows/data/renameOutputReferences';
import {
  updateLlmInsideLoop,
  updateRootLlmNode,
  updateTriggerNode
} from 'src/content/Workflows/data/workflowDocumentUpdates';
import type {
  TemporalWorkflowDocument,
  WorkflowNodeDef
} from 'src/content/Workflows/data/workflowTypes';
import type { WorkflowNodeRunState } from 'src/content/Workflows/Nodes/shared/types';

import {
  hydrateWorkflowDoc,
  patchAgentLiveNestedAction,
  patchAgentLiveRootAction,
  renameWorkflowScopeAction,
  resetForWorkflowAction,
  resetLayoutAction,
  saveAgentNestedAction,
  saveAgentRootAction,
  saveMainAction,
  saveNodeConfigAction,
  saveTriggerAction,
  saveWorkflowIdAction,
  seedTemplateDraft
} from './workflowBuilderActions';

export type WorkflowRunNodeIo = {
  /** Verbatim `input_params` from the latest `node_started` event. */
  input?: unknown;
  /** Verbatim `output` from the latest `node_completed` event. */
  output?: unknown;
  cost?: string;
  status?: WorkflowNodeRunState;
  error?: string;
  iterations?: Record<number, WorkflowRunNodeIterationIo>;
};

export type WorkflowRunNodeIterationIo = Pick<
  WorkflowRunNodeIo,
  'input' | 'output' | 'cost' | 'error'
>;

type WorkflowDraftState = {
  draftsByWorkflowId: Record<string, TemporalWorkflowDocument>;
  /** Server workflow id → canvas node id → last `node_completed` output (in-memory only). */
  runOutputsByServerWorkflowId: Record<
    string,
    Record<string, WorkflowNodeRunOutput>
  >;
  /** Server workflow id → canvas node id → latest live IO from PubSub events. */
  runIoByServerWorkflowId: Record<string, Record<string, WorkflowRunNodeIo>>;
};

const initialState: WorkflowDraftState = {
  draftsByWorkflowId: {},
  runOutputsByServerWorkflowId: {},
  runIoByServerWorkflowId: {}
};

function mergePatchField(
  current: Record<string, unknown> | undefined,
  patchValue: unknown
): Record<string, unknown> {
  const patchObj =
    patchValue && typeof patchValue === 'object'
      ? (patchValue as Record<string, unknown>)
      : null;
  const next: Record<string, unknown> = { ...current, ...patchObj };
  for (const [key, value] of Object.entries(patchObj ?? {})) {
    if (value === undefined) {
      delete next[key];
    }
  }
  return next;
}

function patchNodeConfig(
  doc: TemporalWorkflowDocument,
  nodeId: string,
  patch: Record<string, unknown>
): TemporalWorkflowDocument | null {
  const idx = doc.nodes.findIndex((n) => n.id === nodeId);
  if (idx < 0) {
    return null;
  }
  const current = doc.nodes[idx];
  const nextConfig = mergePatchField(current.config, patch.config);
  const nextInput = mergePatchField(current.input, patch.input);
  const hasInputPatch = Boolean(patch.input && typeof patch.input === 'object');
  const nextNode: WorkflowNodeDef = {
    ...current,
    ...(patch.label !== undefined ? { label: patch.label as string } : {}),
    ...(patch.description !== undefined
      ? { description: patch.description as string }
      : {}),
    config: nextConfig,
    ...(hasInputPatch ? { input: nextInput } : {})
  };
  const nodes = [...doc.nodes];
  nodes[idx] = nextNode;
  const patchedDoc = { ...doc, nodes };
  return applyOutputRefRenames(
    patchedDoc,
    nodeId,
    collectFieldNameRenames(current.config?.fields, nextConfig.fields)
  );
}

const slice = createSlice({
  name: 'workflowDraft',
  initialState,
  reducers: {
    upsertWorkflowDraft(
      state,
      action: PayloadAction<{
        workflowId: string;
        doc: TemporalWorkflowDocument;
      }>
    ) {
      state.draftsByWorkflowId[action.payload.workflowId] = action.payload.doc;
    },
    clearWorkflowDraft(state, action: PayloadAction<{ workflowId: string }>) {
      delete state.draftsByWorkflowId[action.payload.workflowId];
    },
    mergeWorkflowRunOutputs(
      state,
      action: PayloadAction<{
        serverWorkflowId: string;
        outputs: Record<string, WorkflowNodeRunOutput>;
      }>
    ) {
      const { serverWorkflowId, outputs } = action.payload;
      if (!state.runOutputsByServerWorkflowId[serverWorkflowId]) {
        state.runOutputsByServerWorkflowId[serverWorkflowId] = {};
      }
      Object.assign(
        state.runOutputsByServerWorkflowId[serverWorkflowId],
        outputs
      );
    },
    clearWorkflowRunOutputs(
      state,
      action: PayloadAction<{ serverWorkflowId: string }>
    ) {
      delete state.runOutputsByServerWorkflowId[
        action.payload.serverWorkflowId
      ];
    },
    mergeWorkflowRunNodeIo(
      state,
      action: PayloadAction<{
        serverWorkflowId: string;
        nodeId: string;
        io: WorkflowRunNodeIo;
        /** 1-based loop iteration this capture belongs to, for a node inside a
         *  loop body. Kept *alongside* the latest capture, never instead of
         *  it, so nothing that reads the flat fields changes behaviour. */
        iteration?: number;
      }>
    ) {
      const { serverWorkflowId, nodeId, io, iteration } = action.payload;
      if (!state.runIoByServerWorkflowId[serverWorkflowId]) {
        state.runIoByServerWorkflowId[serverWorkflowId] = {};
      }
      const prev =
        state.runIoByServerWorkflowId[serverWorkflowId][nodeId] ?? {};
      const next: WorkflowRunNodeIo = { ...prev, ...io };
      if (iteration !== undefined) {
        next.iterations = {
          ...prev.iterations,
          [iteration]: { ...prev.iterations?.[iteration], ...io }
        };
      }
      state.runIoByServerWorkflowId[serverWorkflowId][nodeId] = next;
    },
    clearWorkflowRunNodeIo(
      state,
      action: PayloadAction<{ serverWorkflowId: string }>
    ) {
      delete state.runIoByServerWorkflowId[action.payload.serverWorkflowId];
    },
    mergeWorkflowRunNodeIoMany(
      state,
      action: PayloadAction<{
        serverWorkflowId: string;
        io: Record<string, WorkflowRunNodeIo>;
      }>
    ) {
      const { serverWorkflowId, io } = action.payload;
      if (!state.runIoByServerWorkflowId[serverWorkflowId]) {
        state.runIoByServerWorkflowId[serverWorkflowId] = {};
      }
      const target = state.runIoByServerWorkflowId[serverWorkflowId];
      for (const [nodeId, nodeIo] of Object.entries(io)) {
        target[nodeId] = { ...target[nodeId], ...nodeIo };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateWorkflowDoc, (state, action) => {
        state.draftsByWorkflowId[action.payload.workflowId] =
          action.payload.doc;
      })
      .addCase(seedTemplateDraft, (state, action) => {
        if (!state.draftsByWorkflowId[action.payload.workflowId]) {
          state.draftsByWorkflowId[action.payload.workflowId] =
            action.payload.doc;
        }
      })
      .addCase(saveWorkflowIdAction, (state, action) => {
        const { workflowId, newId } = action.payload;
        const doc = state.draftsByWorkflowId[workflowId];
        if (!doc) {
          return;
        }
        state.draftsByWorkflowId[workflowId] = { ...doc, workflow_id: newId };
      })
      .addCase(saveTriggerAction, (state, action) => {
        const { workflowId, triggerId, payload } = action.payload;
        const doc = state.draftsByWorkflowId[workflowId];
        if (!doc) {
          return;
        }
        state.draftsByWorkflowId[workflowId] = updateTriggerNode(
          doc,
          triggerId,
          payload
        );
      })
      .addCase(saveAgentRootAction, (state, action) => {
        const { workflowId, nodeId, payload } = action.payload;
        const doc = state.draftsByWorkflowId[workflowId];
        if (!doc) {
          return;
        }
        state.draftsByWorkflowId[workflowId] = updateRootLlmNode(
          doc,
          nodeId,
          payload
        );
      })
      .addCase(saveAgentNestedAction, (state, action) => {
        const { workflowId, loopId, nodeId, payload } = action.payload;
        const doc = state.draftsByWorkflowId[workflowId];
        if (!doc) {
          return;
        }
        state.draftsByWorkflowId[workflowId] = updateLlmInsideLoop(
          doc,
          loopId,
          nodeId,
          payload
        );
      })
      .addCase(patchAgentLiveRootAction, (state, action) => {
        const { workflowId, nodeId, payload } = action.payload;
        const doc = state.draftsByWorkflowId[workflowId];
        if (!doc) {
          return;
        }
        state.draftsByWorkflowId[workflowId] = updateRootLlmNode(
          doc,
          nodeId,
          payload
        );
      })
      .addCase(patchAgentLiveNestedAction, (state, action) => {
        const { workflowId, loopId, nodeId, payload } = action.payload;
        const doc = state.draftsByWorkflowId[workflowId];
        if (!doc) {
          return;
        }
        state.draftsByWorkflowId[workflowId] = updateLlmInsideLoop(
          doc,
          loopId,
          nodeId,
          payload
        );
      })
      .addCase(saveNodeConfigAction, (state, action) => {
        const { workflowId, nodeId, patch } = action.payload;
        const doc = state.draftsByWorkflowId[workflowId];
        if (!doc) {
          return;
        }
        const next = patchNodeConfig(doc, nodeId, patch);
        if (next) {
          state.draftsByWorkflowId[workflowId] = next;
        }
      })
      .addCase(saveMainAction, (state, action) => {
        state.draftsByWorkflowId[action.payload.workflowId] =
          action.payload.doc;
      })
      .addCase(resetLayoutAction, (state, action) => {
        state.draftsByWorkflowId[action.payload.workflowId] =
          action.payload.doc;
      })
      .addCase(resetForWorkflowAction, (state, action) => {
        delete state.draftsByWorkflowId[action.payload.workflowId];
      })
      .addCase(renameWorkflowScopeAction, (state, action) => {
        const { from, to } = action.payload;
        const doc = state.draftsByWorkflowId[from];
        if (from === to || !doc || state.draftsByWorkflowId[to]) {
          return;
        }
        state.draftsByWorkflowId[to] = doc;
        delete state.draftsByWorkflowId[from];
      });
  }
});

export const reducer = slice.reducer;
export const {
  upsertWorkflowDraft,
  clearWorkflowDraft,
  mergeWorkflowRunOutputs,
  clearWorkflowRunOutputs,
  mergeWorkflowRunNodeIo,
  clearWorkflowRunNodeIo,
  mergeWorkflowRunNodeIoMany
} = slice.actions;
